import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import getPatientData from "./getPatientData";
import getPatientMergeTarget from "./getPatientMergeTarget";
import { Patient } from "./types";
import useTranslations from "./useTranslations";
import { createSupabaseClient } from "../../../hooks/useSupabase";

// The merge screens render this drawer as an overlay, where the MRN lives in
// component state instead of the URL — they pass onMergeRedirect so an
// already-merged patient can be swapped for its merge target without a
// navigation (which would unmount the merge screen and lose its state).
const usePatientData = (
  patientMrn?: string,
  onMergeRedirect?: (targetMrn: string) => void,
) => {
  const { translations } = useTranslations();

  const supabase = useMemo(() => createSupabaseClient(), []);

  const { defaultError, errorPatientNotFound } = translations;

  const [isLoading, setIsLoading] = useState(false);

  const [data, setData] = useState<Patient | null>(null);

  const [error, setError] = useState<string | null>(null);

  const navigate = useNavigate();
  const location = useLocation();

  const fetchPatient = useCallback(async () => {
    if (!patientMrn) return;

    try {
      setIsLoading(true);
      let patient: Patient | null = null;

      try {
        patient = await getPatientData({ mrn: patientMrn, supabase });
      } catch (err) {
        throw new Error(defaultError);
      }

      if (!patient) {
        throw new Error(errorPatientNotFound);
      }

      // Direct link to an already-merged (archived) patient — redirect to
      // whoever it was (possibly transitively) merged into instead of
      // showing the archived record.
      if (patient.is_archived) {
        const targetMrn = await getPatientMergeTarget(supabase, patient.id);
        if (targetMrn) {
          if (onMergeRedirect) {
            onMergeRedirect(targetMrn);
          } else {
            navigate(location.pathname.replace(patientMrn, targetMrn), {
              replace: true,
            });
          }
          return;
        }
      }

      setData(patient);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setIsLoading(false);
    }
  }, [
    defaultError,
    errorPatientNotFound,
    patientMrn,
    supabase,
    navigate,
    location.pathname,
    onMergeRedirect,
  ]);

  useEffect(() => {
    fetchPatient();
  }, [fetchPatient]);

  return { isLoading, error, data, refetch: fetchPatient };
};

export default usePatientData;
